/**
 * Dates and times, on Temporal.
 *
 * Two types that must never be mixed:
 *
 *   PlainDate — a calendar date. Arrival, departure, blackouts, the calendar.
 *   Instant   — a moment (a JS Date, always UTC). Arrival time, audit stamps.
 *
 * Temporal enforces that separation in the type system, which is the whole
 * reason for using it: you cannot accidentally subtract two timestamps and call
 * the result a number of nights. The last Sunday of March is 23 hours long, and
 * that arithmetic silently loses a night.
 *
 * `temporal-polyfill` is a polyfill by design — Node 22 and 24 have no native
 * Temporal. When Node ships it, delete the import and the dependency; the API
 * is identical.
 */

import { Temporal } from "temporal-polyfill";

export const BUILDING_TIMEZONE = "Europe/Brussels";

/** A calendar date. No time, no zone. Serialises as `YYYY-MM-DD`. */
export type PlainDate = Temporal.PlainDate;

/** Parse a `YYYY-MM-DD` date. Throws on malformed or impossible dates. */
export function plainDate(value: string): PlainDate {
  return Temporal.PlainDate.from(value, { overflow: "reject" });
}

/**
 * Order two calendar dates: negative if `a` is earlier, 0 if equal.
 *
 * Use this and never compare the `toString()` values. ISO-8601 sorts
 * lexicographically only within four-digit years — Temporal renders year 10000 as
 * `+010000-12-31`, and `"2027-01-01" < "+010000-12-31"` is false.
 */
export function compareDates(a: PlainDate, b: PlainDate): number {
  return Temporal.PlainDate.compare(a, b);
}

/**
 * Nights between arrival and departure.
 *
 * Worked example: aankomst vrijdag 2027-07-09, vertrek zondag 2027-07-11 = 2 nachten.
 * A same-day booking (day rental) is 0 nights, not an error.
 */
export function nightsBetween(arrival: PlainDate, departure: PlainDate): number {
  if (Temporal.PlainDate.compare(departure, arrival) < 0) {
    throw new RangeError(`Departure ${departure} is before arrival ${arrival}`);
  }
  return arrival.until(departure, { largestUnit: "day" }).days;
}

/**
 * Rental days for the logiesdecreet threshold: a weekend with two overnights
 * counts as three.
 *
 * ASSUMPTION, not yet sourced: nights + 1. Load-bearing for the 60–120 day
 * registration threshold and for the "50 rentable days in July and August"
 * subsidy condition. Cite the decree article here or correct it before anything
 * reports on it — see docs/BACKLOG.md, question Q8.
 */
export function rentalDays(arrival: PlainDate, departure: PlainDate): number {
  return nightsBetween(arrival, departure) + 1;
}

/**
 * The instant at which a wall-clock time occurs in Brussels.
 *
 * Disambiguation is Temporal's `compatible` default, which matches what this
 * system wants and what the tests pin: a time that does not exist (02:30 on the
 * spring Sunday) resolves forward to 03:30, and a time that happens twice
 * resolves to the first, summer-time occurrence.
 */
export function brusselsInstant(date: PlainDate, time: string): Date {
  const plainTime = Temporal.PlainTime.from(time, { overflow: "reject" });
  return new Date(
    date.toZonedDateTime({ timeZone: BUILDING_TIMEZONE, plainTime }).epochMilliseconds,
  );
}

/** The Brussels calendar date an instant falls on. */
export function brusselsDateOf(instant: Date): PlainDate {
  return Temporal.Instant.fromEpochMilliseconds(instant.getTime())
    .toZonedDateTimeISO(BUILDING_TIMEZONE)
    .toPlainDate();
}

const DATETIME_FORMAT = new Intl.DateTimeFormat("nl-BE", {
  timeZone: BUILDING_TIMEZONE,
  dateStyle: "long",
  timeStyle: "short",
});

/**
 * Render an instant for a Belgian reader: "14 juli 2027 om 18:00".
 *
 * Never `toLocaleString()` with the browser's zone — a renter opening the
 * confirmation abroad must still see Belgian times.
 */
export function formatInBrussels(instant: Date): string {
  if (Number.isNaN(instant.getTime())) {
    throw new TypeError("Cannot format an invalid Date");
  }
  return DATETIME_FORMAT.format(instant);
}

/**
 * Fail loudly at cold start if the runtime cannot do Belgian dates or money.
 *
 * Without full ICU, `nl-BE` falls back to `en-US` and puts "€1,234.56" on a
 * Belgian invoice — a failure that produces output which still looks plausible.
 */
export function assertIntlSupport(): void {
  if (new Intl.NumberFormat("nl-BE").resolvedOptions().locale !== "nl-BE") {
    throw new Error("Runtime does not know the nl-BE locale — full ICU data is required.");
  }
  if (
    brusselsInstant(plainDate("2027-07-15"), "12:00").toISOString() !== "2027-07-15T10:00:00.000Z"
  ) {
    throw new Error(`Runtime does not know ${BUILDING_TIMEZONE} — full ICU data is required.`);
  }
}
